import React, { useState } from 'react';
import {
    BarChart as RechartsBarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Rectangle,
    BarProps
} from 'recharts';
import { Box, Paper, Typography, useTheme, Chip } from '@mui/material';
import { colorUtils } from '../../theme'

interface CustomBarProps extends BarProps {
    // Recharts defines activeShape as a function that receives BarProps (or any)
    // and returns a React element or a boolean/null.
    activeShape?: (props: any) => React.ReactElement | null | boolean;
    // You also included shape, which is often typed as a ReactElement
    shape?: React.ReactElement;
}

export interface BarChartDataItem {
    name: string;
    value: number;
}

export interface BarChartCategory {
    key: string;
    label: string;
    color: string;
    data: BarChartDataItem[];
}

interface BarChartProps {
    categories: BarChartCategory[];
    title?: string;
    subtitle?: string;
    height?: number;
    showGrid?: boolean;
    showTooltip?: boolean;
    xAxisKey?: string;
    barRadius?: number;
    defaultCategory?: string;
}

export const BarChart: React.FC<BarChartProps> = ({
                                                      categories,
                                                      title,
                                                      subtitle,
                                                      height = 250,
                                                      showGrid = true,
                                                      showTooltip = true,
                                                      xAxisKey = 'name',
                                                      barRadius = 8,
                                                      defaultCategory,
                                                  }) => {
    const theme = useTheme();
    const [activeCategory, setActiveCategory] = useState<string>(
        defaultCategory || categories[0]?.key || ''
    );

    const currentCategory = categories.find(cat => cat.key === activeCategory);
    const currentData = currentCategory?.data || [];
    const currentColor = currentCategory?.color || theme.palette.primary.main;

    // Custom bar shape function (will be used for both shape and activeShape)
    const CustomBar = (props: any) => {
        const { fill, x, y, width, height, radius, active } = props;

        const isHovered = active || false;
        const barRadius = Array.isArray(radius) ? radius[0] : 8;

        return (
            <g>
                <Rectangle
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={fill}
                    radius={[barRadius, barRadius, 0, 0]}
                    style={{
                        filter: isHovered ? 'brightness(1.2)' : 'none',
                        transition: 'filter 0.2s ease',
                    }}
                />
            </g>
        );
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || !payload.length) return null;

        return (
            <Paper
                elevation={8}
                sx={{
                    p: 1.5,
                    backgroundColor: `${theme.palette.background.tertiary} !important`,
                    border: `1px solid ${colorUtils.withOpacity(theme.palette.primary.main, 0.3)}`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                    {label}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ color: currentColor }}
                >
                    {currentCategory?.label}: {payload[0]?.value.toLocaleString()}
                </Typography>
            </Paper>
        );
    };

    return (
        <Box sx={{overflow: 'visible'}}>
            {/* Header with title and tabs */}
            <Box sx={{mb: 2}}>
                {(title || subtitle) && (
                    <Box sx={{mb: 2}}>
                        {title && (
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Category tabs */}
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                    {categories.map((category) => (
                        <Chip
                            key={category.key}
                            label={category.label}
                            onClick={() => setActiveCategory(category.key)}
                            sx={{
                                backgroundColor: activeCategory === category.key
                                    ? colorUtils.withOpacity(category.color, 0.15)
                                    : 'transparent',
                                borderColor: activeCategory === category.key
                                    ? category.color
                                    : colorUtils.withOpacity(theme.palette.text.secondary, 0.3),
                                color: activeCategory === category.key
                                    ? category.color
                                    : theme.palette.text.secondary,
                                borderWidth: 1,
                                borderStyle: 'solid',
                                fontWeight: activeCategory === category.key ? 600 : 400,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: theme.palette.background.paper,
                                    borderColor: category.color,
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={height + 40}>
                <RechartsBarChart
                    data={currentData}
                    margin={{top: 10, right: 10, left: 0, bottom: 0}}
                >
                    {showGrid && (
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={colorUtils.withOpacity(theme.palette.text.secondary, 0.15)}
                            vertical={false}
                        />
                    )}
                    <XAxis
                        dataKey={xAxisKey}
                        stroke={theme.palette.text.secondary}
                        style={{
                            fontSize: '0.75rem',
                            fontFamily: theme.typography.fontFamily,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        stroke={theme.palette.text.secondary}
                        style={{
                            fontSize: '0.75rem',
                            fontFamily: theme.typography.fontFamily,
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    {showTooltip && (
                        <Tooltip
                            content={<CustomTooltip/>}
                            cursor={{fill: colorUtils.withOpacity(theme.palette.background.paper, 0.3)}}
                            wrapperStyle={{outline: 'none'}}
                        />
                    )}

                    <Bar
                        dataKey="value"
                        fill={currentColor}
                        shape={<CustomBar radius={[barRadius, barRadius, 0, 0]}/>}
                        radius={[barRadius, barRadius, 0, 0]}
                        maxBarSize={30}
                    />
                </RechartsBarChart>
            </ResponsiveContainer>
        {/*    <style>{`*/}
        {/*    .recharts-bar-rectangle:hover {*/}
        {/*        transform: scale(1.05);*/}
        {/*        transform-origin: center bottom;*/}
        {/*        transition: transform 0.2s ease;*/}
        {/*    }*/}
        {/*`}</style>*/}
        </Box>
    );
};

export default BarChart;